import { useEffect, useRef, useState } from 'react';
import './ProjectShot.css';

// Safety net: if neither `load` nor `error` ever fires (e.g. the browser
// already resolved the image before React hydrated and attached listeners),
// stop showing the skeleton after this long so a stale asset can never look
// "stuck loading" forever.
const STALL_TIMEOUT_MS = 5000;

/**
 * ProjectShot — CLS-safe project thumbnail.
 *
 * The container reserves its box via `aspect-ratio` + explicit width/height,
 * so the layout never shifts while the (build-time generated) WebP resolves.
 * A shimmer skeleton fills the reserved space and cross-fades out on load.
 *
 * Props:
 *   src      - primary image (e.g. the 800w WebP)
 *   srcSet   - responsive candidates, e.g. `${sm} 400w, ${src} 800w`
 *   sizes    - sizes hint matching the grid layout
 *   alt      - accessible description
 *   width    - intrinsic width  (for aspect-ratio + CLS, default 800)
 *   height   - intrinsic height (for aspect-ratio + CLS, default 500)
 */
export default function ProjectShot({
  src,
  srcSet,
  avif,
  avifSm,
  sizes = '(max-width: 600px) 92vw, (max-width: 992px) 45vw, 380px',
  alt,
  width = 800,
  height = 500,
  className = '',
}) {
  const [status, setStatus] = useState('loading'); // loading | loaded | error
  const imgRef = useRef(null);

  // AVIF served first (smallest), WebP <img> as the universal fallback.
  const avifSrcSet = avif ? (avifSm ? `${avifSm} 400w, ${avif} 800w` : avif) : null;

  // Hydration-race guard: after SSR/prerender, the browser may finish
  // fetching (or serve from cache) before React attaches the onLoad/onError
  // listeners below — the `load` event only fires once, so if we missed it
  // the skeleton would sit on top of an already-rendered image forever.
  // `img.complete` tells us the true state regardless of listener timing.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    if (img.complete) {
      // naturalWidth is 0 for a completed-but-broken image (e.g. 404).
      setStatus(img.naturalWidth > 0 ? 'loaded' : 'error');
      return;
    }

    // Belt-and-suspenders: if for any reason neither event ever fires,
    // don't let the skeleton block the image indefinitely.
    const stallTimer = setTimeout(() => {
      setStatus((prev) => (prev === 'loading' ? 'loaded' : prev));
    }, STALL_TIMEOUT_MS);

    return () => clearTimeout(stallTimer);
  }, [src]);

  return (
    <div
      className={`pshot pshot--${status} ${className}`.trim()}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {status !== 'loaded' && <div className="pshot__skeleton" aria-hidden="true" />}

      <picture>
        {avifSrcSet && <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />}
        <img
          ref={imgRef}
          className="pshot__img"
          src={src}
          srcSet={srcSet}
          sizes={srcSet ? sizes : undefined}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
        />
      </picture>
    </div>
  );
}
